// Public chat API client + config mapping.
import { BACKEND_BASE, LANG_IMAGES, LANG_INFO, primarySubtag } from './config';
import { getToken, getAppCheckToken } from './firebase';
import type { ChatConfig, LanguageOption, StateCaptions } from './types';

/**
 * Build the request headers: always the existing `Authorization: Bearer <idToken>`, plus an
 * additive `X-Firebase-AppCheck` header when an App Check token is available. App Check is
 * never allowed to block a request, so a missing/failed token simply omits the header.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const token = await getToken();
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  const appCheckToken = await getAppCheckToken();
  if (appCheckToken) headers['X-Firebase-AppCheck'] = appCheckToken;
  return headers;
}

export interface InteractResponse {
  answer: string;
  supporting_file: string | null;
  answer_audio_url: string | null;
  conversation_id: string;
  transcribed_text: string;
  credits_remaining: number;
  memory_hit?: boolean;
  error?: string;
  error_message?: string;
}

export interface InteractResult {
  status: number;
  data: InteractResponse;
}

export interface ConfigResult {
  status: number;
  config?: ChatConfig;
  error?: string;
}

export async function fetchConfig(piId: string): Promise<ConfigResult> {
  const res = await fetch(`${BACKEND_BASE}/chat/${encodeURIComponent(piId)}/config`, {
    headers: await authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { status: res.status, error: data.error || `HTTP ${res.status}` };
  return { status: res.status, config: mapConfig(data.config ?? {}) };
}

export async function interactAudio(
  piId: string,
  blob: Blob,
  languageCode: string,
  conversationId: string | null,
  history: string,
): Promise<InteractResult> {
  const fd = new FormData();
  const ext = blob.type.includes('webm')
    ? 'webm'
    : blob.type.includes('ogg')
      ? 'ogg'
      : blob.type.includes('mp4')
        ? 'mp4'
        : 'wav';
  fd.append('audio_file', blob, `recording.${ext}`);
  fd.append('language_code', languageCode);
  fd.append('conversation_id', conversationId || '');
  fd.append('conversation_history', history);
  fd.append('input_type', 'audio');
  return postInteract(piId, fd);
}

/**
 * Send a transcript already produced by the live STT stream (stt-stream.ts).
 * input_type 'voice_text' bypasses batch STT on the backend — the stream route
 * charged the STT credits by streamed duration, so each voice turn is
 * transcribed (and billed) exactly once, mirroring the kiosk.
 */
export async function interactVoiceText(
  piId: string,
  text: string,
  languageCode: string,
  conversationId: string | null,
  history: string,
): Promise<InteractResult> {
  const fd = new FormData();
  fd.append('language_code', languageCode);
  fd.append('conversation_id', conversationId || '');
  fd.append('conversation_history', history);
  fd.append('input_type', 'voice_text');
  fd.append('text_input', text);
  return postInteract(piId, fd);
}

export async function interactText(
  piId: string,
  text: string,
  languageCode: string,
  conversationId: string | null,
  history: string,
): Promise<InteractResult> {
  const fd = new FormData();
  fd.append('language_code', languageCode);
  fd.append('conversation_id', conversationId || '');
  fd.append('conversation_history', history);
  fd.append('input_type', 'keyboard');
  fd.append('text_input', text);
  return postInteract(piId, fd);
}

async function postInteract(piId: string, fd: FormData): Promise<InteractResult> {
  // Do NOT set Content-Type — the browser adds the multipart boundary.
  const res = await fetch(`${BACKEND_BASE}/chat/${encodeURIComponent(piId)}/interact`, {
    method: 'POST',
    headers: await authHeaders(),
    body: fd,
  });
  const data = (await res.json().catch(() => ({}))) as InteractResponse;
  return { status: res.status, data };
}

// --- config mapping --------------------------------------------------------

function asArray(v: unknown, fallback: string[]): string[] {
  return Array.isArray(v) && v.length ? (v as string[]) : fallback;
}

/** Numeric suffix of a 'languageN' key, used to order buttons as the admin arranged them. */
function keyOrder(key: string): number {
  const m = key.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
}

function localeMeta(code: string): { label: string; name: string; image: string | null } {
  const sub = primarySubtag(code);
  const info = LANG_INFO[sub];
  return {
    label: info?.label ?? sub.toUpperCase(),
    name: info?.name ?? code,
    image: LANG_IMAGES[sub] ?? null,
  };
}

function mapConfig(raw: Record<string, any>): ChatConfig {
  const langOpts: Record<string, any> = raw.languageoptions || raw.languageOptions || {};

  // Build one button per configured language. Entries are keyed 'language1', 'language2', …
  // in the webui; order by that suffix so the buttons match the admin's arrangement, then
  // backfill any enabledLanguages that lack a full entry. Every locale gets a button —
  // there is no longer a fixed NL/FR/EN allowlist.
  const languages: LanguageOption[] = [];
  const seen = new Set<string>();
  const addLang = (code: unknown, captions: StateCaptions): void => {
    if (typeof code !== 'string' || !code || seen.has(code)) return;
    seen.add(code);
    languages.push({ code, ...localeMeta(code), captions });
  };

  Object.entries(langOpts)
    .filter(([, v]) => v && typeof v === 'object' && typeof v.languagecode === 'string')
    .sort(([a], [b]) => keyOrder(a) - keyOrder(b))
    .forEach(([, v]) =>
      addLang(v.languagecode, {
        wait: asArray(v.wait, ['Ask me a question']),
        listening: asArray(v.listening, ['Listening…']),
        processing: asArray(v.processing, ['Thinking…']),
        eager: asArray(v.eager_for_more, asArray(v.eager, ['Anything else?'])),
      }),
    );
  for (const code of (raw.enabledLanguages || []) as string[]) addLang(code, defaultCaptions());

  return {
    companyName: raw.companyName || '',
    defaultLanguage: raw.defaultLanguage || languages[0]?.code || 'en-US',
    enabledLanguages: raw.enabledLanguages || languages.map((l) => l.code),
    policy: raw.policy || '',
    languages,
    files: Array.isArray(raw.files)
      ? raw.files.map((f: any) => ({
          filename: f.filename,
          url: f.url,
          mobileUrl: f.mobileUrl ?? null,
          videoType: f.videoType,
          category: f.category,
        }))
      : [],
  };
}

function defaultCaptions(): StateCaptions {
  return {
    wait: ['Ask me a question'],
    listening: ['Listening…'],
    processing: ['Thinking…'],
    eager: ['Anything else?'],
  };
}
